import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { AppState } from '../store/store';
import JwtDecode from 'jwt-decode';
import { DecodedToken } from '../auth/authReducer';
import PasswordSettings from './settings/PasswordSettings';
import { MfaRegistrationStatus, MfaRegistrationStatusStatusEnum } from '../api';
import MfaSettings from './settings/MfaSettings';
import './Profile.scss';
import { isFeatureEnabled, ORGANISATION_FEATURE } from '../organisation/feature/helper';
import NotificationSettings from './settings/NotificationSettings';
import { CAN_READ_ALERT_RFI } from '../auth/permissions';
import Has from '../auth/Has';
import { useMe } from '../organisation/useMe';
import SalvPage from '../common/SalvPage';
import { SalvCard } from '@salvapp/ui-components';
import { ExpansionOptions } from '@salvapp/ui-components/build/SalvCard/SalvCard.types';

export const Profile = (): React.JSX.Element => {
  const { token } = useSelector((state: AppState) => ({
    token: state.auth.tokenResponse,
  }));
  const { features } = useMe();

  const [user, setUser] = useState<DecodedToken>();

  const [registrationStatus, setRegistrationStatus] = useState<MfaRegistrationStatus | undefined>();

  const bridgeEnabled =
    isFeatureEnabled(ORGANISATION_FEATURE.BRIDGE_MESSAGING_INCOMING, features) ||
    isFeatureEnabled(ORGANISATION_FEATURE.BRIDGE_MESSAGING_OUTGOING, features);

  useEffect(() => {
    if (token) {
      const decodedToken = JwtDecode(token.accessToken) as DecodedToken;
      setUser(decodedToken);
    }
  }, [token]);

  const initialExpansions = Object.values(Cards).map((_) => false);

  const [expansions, setExpansions] = useState(initialExpansions.map((_, i) => i === 0));

  const getExpansionOptions = (index: number): ExpansionOptions => {
    return {
      isExpanded: expansions[index],
      onToggle: () => {
        const newExpansions = [...initialExpansions];
        newExpansions[index] = !expansions[index];
        setExpansions(newExpansions);
      },
    };
  };

  const onRegistrationStatusUpdate = useCallback(async (status: MfaRegistrationStatus) => {
    setRegistrationStatus(status);
  }, []);

  return (
    <SalvPage title={`Profile - ${user?.sub}`} className={'profile'}>
      <SalvCard expansionOptions={getExpansionOptions(Cards.PASSWORD)}>
        <SalvCard.Header>Change Password</SalvCard.Header>
        <SalvCard.Body>
          <PasswordSettings />
        </SalvCard.Body>
      </SalvCard>

      <SalvCard expansionOptions={getExpansionOptions(Cards.MFA)}>
        <SalvCard.Header
          actions={
            registrationStatus?.status === MfaRegistrationStatusStatusEnum.NONE && registrationStatus?.required ? (
              <span className='profile-requires-attention'>
                Two-Factor Authentication is required to access the platform
              </span>
            ) : (
              registrationStatus &&
              registrationStatus.status !== MfaRegistrationStatusStatusEnum.FEATURE_NOT_ENABLED && (
                <span
                  className={
                    registrationStatus?.status === MfaRegistrationStatusStatusEnum.COMPLETE
                      ? 'profile-status'
                      : 'profile-requires-attention'
                  }
                >
                  Status: {registrationStatus?.status.toLowerCase()}
                </span>
              )
            )
          }
        >
          Two-Factor Authentication
        </SalvCard.Header>
        <SalvCard.Body>
          <MfaSettings onRegistrationStatusUpdate={onRegistrationStatusUpdate} />
        </SalvCard.Body>
      </SalvCard>

      {bridgeEnabled && (
        <Has allPermissions={[CAN_READ_ALERT_RFI]}>
          <SalvCard expansionOptions={getExpansionOptions(Cards.BRIDGE_NOTIFICATIONS)}>
            <SalvCard.Header>Bridge collaborative investigation notifications</SalvCard.Header>
            <SalvCard.Body>
              <NotificationSettings />
            </SalvCard.Body>
          </SalvCard>
        </Has>
      )}
    </SalvPage>
  );
};

const Cards = {
  PASSWORD: 0,
  MFA: 1,
  BRIDGE_NOTIFICATIONS: 2,
} as const;

export default Profile;
